import { AuthenticationSettings } from "@account/interfaces";
import { Injectable, signal, computed } from "@angular/core";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { ClaimsTypes } from "@bbwt/modules/security/claims-types";
import { CoreRole } from "@main/roles";
import { switchMap } from "rxjs";
import { PatientLogin, UserLogin, UserService } from ".";
import { IOrganization, OrganizationService } from "@main/companies";
import { IGoverningBody } from "@project/governingbody/governingbody";
import { GoverningBodyService } from "@project/governingbody/governingbody.service";
import { UserClaimsStore } from "./user-claims-store";
import { HomeScreenMode } from "@main/users/home-screen-mode";
import {Permissions} from "@main/permissions/permissions";

@Injectable({
    providedIn: "root"
})
export class UserHelperService {
    private static readonly PatientLoginType = "PatientLogin";
    private static readonly UserLoginType = "UserLogin";

    public static readonly ClaimsUpdatedEventName = "UserClaimsUpdated";

    protected readonly _claims = signal(this.claimsStore.claims);
    private readonly _loadingClaims = signal(false);

    public readonly loadingClaims = computed(() => this._loadingClaims());
    public readonly hashedOrganizationIdSignal = computed(() => this.findFirstValue(ClaimsTypes.HashedTenantId));

    constructor(
        private readonly broadcastService: BroadcastService,
        private readonly userService: UserService,
        private readonly claimsStore: UserClaimsStore
    ) {
        this.subscribeEvents();
    }

    initialize() {
        return this.loadClaims();
    }

    refresh() {
        return this.loadClaims();
    }

    private async loadClaims() {
        if (this.userService.isLogged) {
            this._loadingClaims.set(true);
            try {
                this._claims.set(await this.claimsStore.getClaims());
                this.broadcastService.broadcast(UserHelperService.ClaimsUpdatedEventName);
            } finally {
                this._loadingClaims.set(false);
            }
        }
    }

    private subscribeEvents() {
        this.broadcastService
            .on<AuthenticationSettings>(UserService.CurrentUserChangedEventName)
            .pipe(switchMap(_ => this.loadClaims()))
            .subscribe(() => {});

        this.broadcastService.on<any>(OrganizationService.OrganizationChangedEventName).subscribe(async (org: IOrganization) => {
            if (this.organizationId == `${org.id_original}`) {
                await this.refresh();
            }
        });

        this.broadcastService.on<any>(OrganizationService.ModulesChangedEventName).subscribe(async (orgId: number) => {
            if (this.organizationId == `${orgId}`) {
                await this.refresh();
            }
        });

        this.broadcastService.on<IGoverningBody>(GoverningBodyService.GovBodyChangedEventName).subscribe(async govBody => {
            if (this.hashedGoverningBodyId == govBody.id) {
                await this.refresh();
            }
        });
    }

    private getAllValues(claimType: string) {
        return this._claims().filter(c => c.type === claimType).map(c => c.value);
    }

    hasAccess(claimType: string, values: string[]) {
        return this.getAllValues(claimType).some(value => values.includes(value));
    }

    hasClaim(claimType: string, claimValue: string) {
        return this._claims().some(c => c.type === claimType && c.value === claimValue);
    }

    findFirstValue(claimType: string) {
        return this._claims().find(c => c.type === claimType)?.value;
    }

    hasRole(...role: string[]) {
        return this.hasAccess(ClaimsTypes.Role, role);
    }

    isInGroup(...group: string[]) {
        return this.hasAccess(ClaimsTypes.BelongsToGroup, group);
    }

    hasPermission(...permission: string[]) {
        return this.hasAccess(ClaimsTypes.UserPermission, permission);
    }

    hasModuleEnabled(...module: string[]) {
        return this.hasAccess(ClaimsTypes.MiProEnabledModule, module);
    }

    hasFormModuleEnabled(...module: string[]) {
        if (!this.hasModuleEnabled(...module)) return false;

        // TODO: This should be fixed later.
        //  There should be a robust way to check if the user has permissions on a form module
        //  (for example, a explicit relation between modules and permissions), instead of comparing by name.
        return module.every(module =>
            this.hasPermission(`${module} - Read`) ||
            this.hasPermission(`${module} - Write`));
    }

    hasOrganization(...organizationId: string[]) {
        const allTenants = this.findFirstValue(ClaimsTypes.AllTenantIds) ?? "";
        return organizationId.some(id => allTenants.includes(id));
    }

    /** Helpers */
    get organizationId() {
        return this.findFirstValue(ClaimsTypes.TenantId);
    }

    get isPassportOrganization() {
        return this.findFirstValue(ClaimsTypes.TenantId) === "_PASSPORT";
    }

    get governingBodyId() {
        return this.findFirstValue(ClaimsTypes.TenantGoverningBodyId);
    }

    get hashedGoverningBodyId() {
        return this.findFirstValue(ClaimsTypes.HashedTenantGoverningBodyId);
    }

    get hashedOrganizationId() {
        return this.findFirstValue(ClaimsTypes.HashedTenantId);
    }

    get selfDirectedGymRecoveryPermission() {
        return this.findFirstValue(ClaimsTypes.SelfDirectedGymRecoveryPermission)?.trim().toLowerCase() === "true";
    }

    get permissions() {
        return this.getAllValues(ClaimsTypes.UserPermission);
    }

    get roles() {
        return this.getAllValues(ClaimsTypes.Role);
    }

    get isTopAdmin() {
        return this.isSuperAdmin || this.isMiProAdmin;
    }

    get isSuperAdmin() {
        return this.hasRole(CoreRole.SuperAdmin);
    }

    get isMiProAdmin() {
        return this.hasRole(CoreRole.MiProAdmin);
    }

    get isPatient() {
        return this.hasClaim(ClaimsTypes.LoginType, UserHelperService.PatientLoginType);
    }

    get isUser() {
        return this.hasClaim(ClaimsTypes.LoginType, UserHelperService.UserLoginType);
    }

    get isGenericPatient() {
        return this.hasRole(CoreRole.GenericPatientLogin);
    }

    get isGenericLogin() {
        return this.hasRole(CoreRole.GenericLogin);
    }

    get isReadPatientPermission() {
        return this.hasPermission(Permissions.ReadPatient);
    }

    get isDeletePatientArchivePermission() {
        return this.hasPermission(Permissions.ArchivePatient);
    }

    get isClinician() {
        return this.isUser && (this.isReadPatientPermission || this.isDeletePatientArchivePermission);
    }

    get realPersonId() {
        return this.findFirstValue(ClaimsTypes.RealPersonId);
    }

    get loginId() {
        return this.findFirstValue(ClaimsTypes.NameIdentifier);
    }

    get userId() {
        return this.isUser ? (this.userService.currentUser as UserLogin).userId : null;
    }

    get patientId() {
        return this.isPatient ? (this.userService.currentUser as PatientLogin).patientId : null;
    }

    get email() {
        return this.findFirstValue(ClaimsTypes.EmailAddress);
    }

    get enabledModules() {
        return this.getAllValues(ClaimsTypes.MiProEnabledModule);
    }

    get homeDashboardToggleEnabled() {
        return this.hasClaim(ClaimsTypes.HomeDashboardToggleEnabled, "true");
    }

    get defaultHomeScreenMode(): HomeScreenMode {
        return +this.findFirstValue(ClaimsTypes.DefaultHomeScreenMode);
    }

    isCurrentUserOldEnough(minimumAllowedAge: number): boolean {
        const currentUser = this.userService.currentUser;
        if (currentUser != null) {
            const dob = new Date(currentUser.dateOfBirth);
            const currentDate = new Date();
            let age = currentDate.getUTCFullYear() - dob.getUTCFullYear();
            if (dob.setUTCFullYear(dob.getUTCFullYear() + age) > currentDate.getUTCFullYear()) {
                age--;
            }
            return age >= minimumAllowedAge;
        }
        return false;
    }
}